'use client';

import { createContext, Suspense, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { interactionResponse } from '@zillow/yield-callback';
import type { ReactNode } from 'react';
import dynamic from 'next/dynamic';
import { Toast, useToast } from '@zillow/constellation-10';
import { useTranslations } from 'next-intl';
import type { ExtendedListing } from '@/src/lib/api/endpoints/listing';
import { tryNativeShare } from '@/src/lib/utils/shareUtils';
import { analytics } from '@/src/components/features/property/lib';
import { emitTrackHdpNativeShareEvent } from '@/src/analytics/hotpads/events';
import type { PropertyDetailListing } from '@/src/components/features/property/types';
import type { ContactSubmissionContext } from '@/src/components/features/property/ContactForm/types';
import { useAddUserItem, useRemoveUserItem } from '@/src/lib/hooks/query/useUserItems';
import { useUserListings } from '@/src/lib/hooks/query/useUserListings';
import { useCurrentUser } from '@/src/lib/hooks/query/useUserQuery';
import {
  DEFAULT_LISTING_ACTION_ANALYTICS,
  getListingActionAlias,
  getListingActionClickstreamListing,
  type ListingActionAnalyticsContext,
  listingHasFavoriteUserItem,
  listingHasHiddenUserItem,
  type ListingLike,
} from './listingActionUtils';
import { ListingActionSignInModal } from './ListingActionSignInModal';

const ContactFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ContactForm/ContactFormOverlay').then((mod) => mod.ContactFormOverlay),
);

const PostContactFlowModal = dynamic(() =>
  import('@/src/components/features/property/ContactForm/PostContactFlowModal').then((mod) => mod.PostContactFlowModal),
);

const ShareFormOverlay = dynamic(() =>
  import('@/src/components/features/property/ShareForm/ShareFormOverlay').then((mod) => mod.ShareFormOverlay),
);

const ReportListingOverlay = dynamic(() =>
  import('@/src/components/features/property/ReportListingOverlay/ReportListingOverlay').then(
    (mod) => mod.ReportListingOverlay,
  ),
);

type ListingActionListing = PropertyDetailListing | ExtendedListing | ListingLike;

type ListingActionOpenOptions = Partial<ListingActionAnalyticsContext> & {
  title?: string;
};

type ListingActionContextValue = {
  openContact: (listing: ListingActionListing | null | undefined, options?: ListingActionOpenOptions) => void;
  openShare: (listing: ListingActionListing | null | undefined) => void;
  openReport: (listing: ListingActionListing | null | undefined) => void;
  isListingSaved: (listing: ListingLike | null | undefined) => boolean;
  isListingSavePending: (listing: ListingLike | null | undefined) => boolean;
  isListingHidden: (listing: ListingLike | null | undefined) => boolean;
  reconcileFavoriteOverride: (listing: ListingLike | null | undefined) => void;
  toggleSaveListing: (
    listing: ListingLike | null | undefined,
    analyticsContext?: ListingActionAnalyticsContext,
  ) => void;
  toggleHideListing: (
    listing: ListingLike | null | undefined,
    analyticsContext?: ListingActionAnalyticsContext,
  ) => void;
};

type ActiveContact = {
  listing: PropertyDetailListing | ExtendedListing | null | undefined;
  title: string;
};

type ActiveShare = {
  listing: PropertyDetailListing | ExtendedListing;
};

type ActiveReport = {
  listing: PropertyDetailListing | ExtendedListing;
};

type PendingHide = {
  alias: string;
  analyticsContext: ListingActionAnalyticsContext;
};

type PendingFavorite = {
  alias: string;
  analyticsContext: ListingActionAnalyticsContext;
  requestedAt: number;
};

const ListingActionContext = createContext<ListingActionContextValue | null>(null);
const PENDING_FAVORITE_STORAGE_KEY = 'hotpads-pending-favorite';
const PENDING_FAVORITE_MAX_AGE_MS = 30 * 60 * 1000;
const PENDING_FAVORITE_REDIRECT_PARAM = 'favorite';
const MAX_PENDING_FAVORITE_REDIRECT_PARAMS = 5;
const FAVORITE_OVERRIDES_STORAGE_KEY = 'hotpads-favorite-overrides';
const FAVORITE_OVERRIDE_EVENT_STORAGE_KEY = 'hotpads-favorite-override-event';
const FAVORITE_OVERRIDES_MAX_AGE_MS = 30 * 60 * 1000;

type StoredFavoriteOverride = {
  saved: boolean;
  updatedAt: number;
};

type StoredFavoriteOverrideEvent = StoredFavoriteOverride & {
  alias: string;
};

function getCurrentRelativeUrl(): string {
  if (typeof window === 'undefined') return '/';
  return `${window.location.pathname}${window.location.search}${window.location.hash}`;
}

function mergePendingFavorites(...favoriteGroups: Array<Array<PendingFavorite>>): Array<PendingFavorite> {
  const pendingFavoritesByAlias = new Map<string, PendingFavorite>();
  favoriteGroups.flat().forEach((pendingFavorite) => {
    pendingFavoritesByAlias.set(pendingFavorite.alias, pendingFavorite);
  });
  return Array.from(pendingFavoritesByAlias.values());
}

function withPendingFavoriteRedirectParams(path: string, aliases: Array<string>): string {
  const url = new URL(path, 'https://hotpads.com');
  url.searchParams.delete(PENDING_FAVORITE_REDIRECT_PARAM);
  aliases
    .slice(0, MAX_PENDING_FAVORITE_REDIRECT_PARAMS)
    .forEach((alias) => url.searchParams.append(PENDING_FAVORITE_REDIRECT_PARAM, alias));
  return `${url.pathname}${url.search}${url.hash}`;
}

function readPendingFavorites(): Array<PendingFavorite> {
  if (typeof window === 'undefined') return [];

  try {
    const stored = window.sessionStorage.getItem(PENDING_FAVORITE_STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored) as PendingFavorite | Array<PendingFavorite>;
    const storedPendingFavorites = Array.isArray(parsed) ? parsed : [parsed];
    const freshPendingFavorites = mergePendingFavorites(
      storedPendingFavorites.filter(
        (pendingFavorite) =>
          Boolean(pendingFavorite?.alias) && Date.now() - pendingFavorite.requestedAt <= PENDING_FAVORITE_MAX_AGE_MS,
      ),
    );
    if (freshPendingFavorites.length === 0) {
      window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
      return [];
    }
    return freshPendingFavorites;
  } catch {
    window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
    return [];
  }
}

function readPendingFavoritesFromUrl(): Array<PendingFavorite> {
  if (typeof window === 'undefined') return [];

  const url = new URL(window.location.href);
  const aliases = Array.from(new Set(url.searchParams.getAll(PENDING_FAVORITE_REDIRECT_PARAM).filter(Boolean))).slice(
    0,
    MAX_PENDING_FAVORITE_REDIRECT_PARAMS,
  );
  if (aliases.length === 0) return [];

  url.searchParams.delete(PENDING_FAVORITE_REDIRECT_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);

  const requestedAt = Date.now();
  return aliases.map((alias) => ({
    alias,
    analyticsContext: DEFAULT_LISTING_ACTION_ANALYTICS,
    requestedAt,
  }));
}

function writePendingFavorites(pendingFavorites: Array<PendingFavorite>): void {
  if (typeof window === 'undefined') return;

  try {
    window.sessionStorage.setItem(PENDING_FAVORITE_STORAGE_KEY, JSON.stringify(pendingFavorites));
  } catch {
    window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
  }
}

function clearPendingFavorites(): void {
  if (typeof window === 'undefined') return;
  window.sessionStorage.removeItem(PENDING_FAVORITE_STORAGE_KEY);
}

function readFavoriteOverrides(): Record<string, boolean> {
  if (typeof window === 'undefined') return {};

  try {
    const stored = window.sessionStorage.getItem(FAVORITE_OVERRIDES_STORAGE_KEY);
    if (!stored) return {};

    const parsed = JSON.parse(stored) as Record<string, StoredFavoriteOverride>;
    const now = Date.now();
    const freshEntries = Object.entries(parsed).filter(
      ([, override]) =>
        typeof override?.saved === 'boolean' &&
        typeof override.updatedAt === 'number' &&
        now - override.updatedAt <= FAVORITE_OVERRIDES_MAX_AGE_MS,
    );

    if (freshEntries.length !== Object.keys(parsed).length) {
      if (freshEntries.length === 0) {
        window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
      } else {
        window.sessionStorage.setItem(FAVORITE_OVERRIDES_STORAGE_KEY, JSON.stringify(Object.fromEntries(freshEntries)));
      }
    }

    return Object.fromEntries(freshEntries.map(([alias, override]) => [alias, override.saved]));
  } catch {
    window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
    return {};
  }
}

function writeFavoriteOverrides(overrides: Record<string, boolean>): void {
  if (typeof window === 'undefined') return;

  try {
    const entries = Object.entries(overrides);
    if (entries.length === 0) {
      window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
      return;
    }

    const updatedAt = Date.now();
    window.sessionStorage.setItem(
      FAVORITE_OVERRIDES_STORAGE_KEY,
      JSON.stringify(Object.fromEntries(entries.map(([alias, saved]) => [alias, { saved, updatedAt }]))),
    );
  } catch {
    window.sessionStorage.removeItem(FAVORITE_OVERRIDES_STORAGE_KEY);
  }
}

function writeFavoriteOverrideEvent(alias: string, saved: boolean): void {
  if (typeof window === 'undefined') return;

  try {
    window.localStorage.setItem(
      FAVORITE_OVERRIDE_EVENT_STORAGE_KEY,
      JSON.stringify({ alias, saved, updatedAt: Date.now() }),
    );
  } catch {
    window.localStorage.removeItem(FAVORITE_OVERRIDE_EVENT_STORAGE_KEY);
  }
}

function readFavoriteOverrideEvent(value: string | null): StoredFavoriteOverrideEvent | null {
  if (!value) return null;

  try {
    const parsed = JSON.parse(value) as StoredFavoriteOverrideEvent;
    if (!parsed.alias || typeof parsed.saved !== 'boolean') return null;
    if (typeof parsed.updatedAt !== 'number') return null;
    if (Date.now() - parsed.updatedAt > FAVORITE_OVERRIDES_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function ListingActionProvider({ children }: { children: ReactNode }) {
  const tAuth = useTranslations('auth');
  const tHdp = useTranslations('hdp');
  const { data: currentUser } = useCurrentUser();
  const { data: favoriteListingsData } = useUserListings('favorite', {
    enabled: currentUser?.loggedIn === true,
  });
  const { data: hiddenListingsData } = useUserListings('hidden', {
    enabled: currentUser?.loggedIn === true,
  });
  const favoritedAliases = useMemo(
    () => new Set((favoriteListingsData?.listings ?? []).map((l) => l.aliasEncoded).filter((a): a is string => !!a)),
    [favoriteListingsData],
  );
  const hiddenAliases = useMemo(
    () => new Set((hiddenListingsData?.listings ?? []).map((l) => l.aliasEncoded).filter((a): a is string => !!a)),
    [hiddenListingsData],
  );
  const addUserItem = useAddUserItem();
  const removeUserItem = useRemoveUserItem();
  const { enqueueToast } = useToast();
  const [activeContact, setActiveContact] = useState<ActiveContact | null>(null);
  const [activeShare, setActiveShare] = useState<ActiveShare | null>(null);
  const [activeReport, setActiveReport] = useState<ActiveReport | null>(null);
  const [postContactContext, setPostContactContext] = useState<ContactSubmissionContext | null>(null);
  const [favoriteOverrides, setFavoriteOverrides] = useState<Record<string, boolean>>({});
  const [pendingFavorites, setPendingFavorites] = useState<Array<PendingFavorite>>([]);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authRedirectPath, setAuthRedirectPath] = useState('/');
  // Hide is login-gated like favorites, but uses lighter in-memory optimistic
  // state (no cross-tab / redirect persistence) — a re-click after a redirect
  // sign-in is acceptable for v1.
  const [hiddenOverrides, setHiddenOverrides] = useState<Record<string, boolean>>({});
  const [pendingHide, setPendingHide] = useState<PendingHide | null>(null);
  const [hideAuthModalOpen, setHideAuthModalOpen] = useState(false);
  const [hideAuthRedirectPath, setHideAuthRedirectPath] = useState('/');

  useEffect(() => {
    const storedPendingFavorites = readPendingFavorites();
    const urlPendingFavorites = readPendingFavoritesFromUrl();
    const nextPendingFavorites = mergePendingFavorites(urlPendingFavorites, storedPendingFavorites);
    if (nextPendingFavorites.length > 0) {
      setPendingFavorites(nextPendingFavorites);
    }
    setFavoriteOverrides((current) => ({ ...readFavoriteOverrides(), ...current }));
  }, []);

  useEffect(() => {
    const handleFavoriteOverrideEvent = (event: StorageEvent) => {
      if (event.key !== FAVORITE_OVERRIDE_EVENT_STORAGE_KEY) return;
      const override = readFavoriteOverrideEvent(event.newValue);
      if (!override) return;

      setFavoriteOverrides((current) => {
        const next = { ...current, [override.alias]: override.saved };
        writeFavoriteOverrides(next);
        return next;
      });
    };

    window.addEventListener('storage', handleFavoriteOverrideEvent);
    return () => window.removeEventListener('storage', handleFavoriteOverrideEvent);
  }, []);

  // Sign-out in this tab: drop optimistic overrides so they can't leak to the next account.
  const loggedIn = currentUser?.loggedIn === true;
  const wasLoggedInRef = useRef(loggedIn);
  useEffect(() => {
    if (wasLoggedInRef.current && !loggedIn) {
      setFavoriteOverrides({});
      writeFavoriteOverrides({});
      setHiddenOverrides({});
    }
    wasLoggedInRef.current = loggedIn;
  }, [loggedIn]);

  const updateFavoriteOverride = useCallback((alias: string, saved: boolean, broadcast = true) => {
    setFavoriteOverrides((current) => {
      const next = { ...current, [alias]: saved };
      writeFavoriteOverrides(next);
      return next;
    });
    if (broadcast) {
      writeFavoriteOverrideEvent(alias, saved);
    }
  }, []);

  const reconcileFavoriteOverride = useCallback(
    (listing: ListingLike | null | undefined) => {
      if (!currentUser?.loggedIn || !listing?.userItemTypes) return;
      const alias = getListingActionAlias(listing);
      if (!alias) return;

      const override = favoriteOverrides[alias];
      if (override === undefined || override !== listingHasFavoriteUserItem(listing)) return;

      setFavoriteOverrides((current) => {
        if (current[alias] !== override) return current;
        const next = { ...current };
        delete next[alias];
        writeFavoriteOverrides(next);
        return next;
      });
    },
    [currentUser?.loggedIn, favoriteOverrides],
  );

  const showFavoriteError = useCallback(() => {
    enqueueToast(
      <Toast tone="critical" role="status">
        {tAuth('favoriteSaveError')}
      </Toast>,
    );
  }, [enqueueToast, tAuth]);

  const addFavorite = useCallback(
    async (pending: PendingFavorite) => {
      updateFavoriteOverride(pending.alias, true);

      try {
        await addUserItem.mutateAsync({ type: 'favorite', alias: pending.alias });
      } catch {
        updateFavoriteOverride(pending.alias, false);
        showFavoriteError();
      }
    },
    [addUserItem, showFavoriteError, updateFavoriteOverride],
  );

  const removeFavorite = useCallback(
    async (alias: string) => {
      updateFavoriteOverride(alias, false);

      try {
        await removeUserItem.mutateAsync({ type: 'favorite', alias });
      } catch {
        updateFavoriteOverride(alias, true);
        showFavoriteError();
      }
    },
    [removeUserItem, showFavoriteError, updateFavoriteOverride],
  );

  const completePendingFavorites = useCallback(() => {
    const favoritesToAdd = pendingFavorites.length > 0 ? pendingFavorites : readPendingFavorites();
    if (favoritesToAdd.length === 0) return;

    setPendingFavorites([]);
    clearPendingFavorites();
    setAuthModalOpen(false);
    favoritesToAdd.forEach((pendingFavorite) => void addFavorite(pendingFavorite));
  }, [addFavorite, pendingFavorites]);

  useEffect(() => {
    if (pendingFavorites.length === 0) return;

    setAuthRedirectPath(
      withPendingFavoriteRedirectParams(
        getCurrentRelativeUrl(),
        pendingFavorites.map((pendingFavorite) => pendingFavorite.alias),
      ),
    );

    if (currentUser?.loggedIn) {
      completePendingFavorites();
      return;
    }

    if (currentUser === null) {
      setAuthModalOpen(true);
    }
  }, [completePendingFavorites, currentUser, pendingFavorites]);

  const isListingHidden = useCallback(
    (listing: ListingLike | null | undefined) => {
      if (!currentUser?.loggedIn) return false;
      const alias = getListingActionAlias(listing);
      if (!alias) return false;

      const override = hiddenOverrides[alias];
      if (override !== undefined) return override;

      return hiddenAliases.has(alias) || listingHasHiddenUserItem(listing);
    },
    [currentUser?.loggedIn, hiddenAliases, hiddenOverrides],
  );

  const updateHiddenOverride = useCallback((alias: string, hidden: boolean) => {
    setHiddenOverrides((current) => ({ ...current, [alias]: hidden }));
  }, []);

  const showHideError = useCallback(() => {
    enqueueToast(
      <Toast tone="critical" role="status">
        {tHdp('hideError')}
      </Toast>,
    );
  }, [enqueueToast, tHdp]);

  const addHidden = useCallback(
    async (alias: string, analyticsContext: ListingActionAnalyticsContext) => {
      updateHiddenOverride(alias, true);

      try {
        await addUserItem.mutateAsync({ type: 'hidden', alias });
        analytics.emitTrackHideHomeFromHdpEvent(analyticsContext);
        enqueueToast(
          <Toast tone="success" role="status">
            {tHdp('listingHidden')}
          </Toast>,
        );
      } catch {
        updateHiddenOverride(alias, false);
        showHideError();
      }
    },
    [addUserItem, enqueueToast, showHideError, tHdp, updateHiddenOverride],
  );

  const removeHidden = useCallback(
    async (alias: string, analyticsContext: ListingActionAnalyticsContext) => {
      updateHiddenOverride(alias, false);

      try {
        await removeUserItem.mutateAsync({ type: 'hidden', alias });
        analytics.emitTrackUnhideHomeFromHdpEvent(analyticsContext);
      } catch {
        updateHiddenOverride(alias, true);
        showHideError();
      }
    },
    [removeUserItem, showHideError, updateHiddenOverride],
  );

  const completePendingHide = useCallback(() => {
    if (!pendingHide) return;

    const hideToAdd = pendingHide;
    setPendingHide(null);
    setHideAuthModalOpen(false);
    void addHidden(hideToAdd.alias, hideToAdd.analyticsContext);
  }, [addHidden, pendingHide]);

  useEffect(() => {
    if (!pendingHide) return;

    if (currentUser?.loggedIn) {
      completePendingHide();
      return;
    }

    if (currentUser === null) {
      setHideAuthModalOpen(true);
    }
  }, [completePendingHide, currentUser, pendingHide]);

  const toggleHideListing = useCallback(
    (
      listing: ListingLike | null | undefined,
      analyticsContext: ListingActionAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
    ) => {
      const alias = getListingActionAlias(listing);
      if (!listing || !alias) return;
      const clickstreamListing = getListingActionClickstreamListing(listing);
      const analyticsContextWithListing = { ...analyticsContext, listing: clickstreamListing };

      if (isListingHidden(listing)) {
        void removeHidden(alias, analyticsContextWithListing);
        return;
      }

      if (!currentUser?.loggedIn) {
        setPendingHide({ alias, analyticsContext: analyticsContextWithListing });
        setHideAuthRedirectPath(getCurrentRelativeUrl());
        if (currentUser !== undefined) {
          setHideAuthModalOpen(true);
        }
        return;
      }

      void addHidden(alias, analyticsContextWithListing);
    },
    [addHidden, currentUser, isListingHidden, removeHidden],
  );

  const handleHideAuthModalOpenChange = useCallback((open: boolean) => {
    setHideAuthModalOpen(open);
    if (open) return;
    setPendingHide(null);
  }, []);

  const openReport = useCallback((listing: ListingActionListing | null | undefined) => {
    if (!listing) return;
    setActiveReport({ listing: listing as PropertyDetailListing | ExtendedListing });
  }, []);

  // Yields for INP: paints the tap feedback before mounting the contact modal.
  const openContact = useCallback(
    async (listing: ListingActionListing | null | undefined, options?: ListingActionOpenOptions) => {
      await interactionResponse();
      if (currentUser === undefined) return;

      const { title, ...analyticsOptions } = options ?? {};
      const analyticsContext = {
        ...DEFAULT_LISTING_ACTION_ANALYTICS,
        ...analyticsOptions,
      };
      const clickstreamListing = getListingActionClickstreamListing(listing);

      analytics.emitTrackContactClickEvent({
        ...analyticsContext,
        listing: clickstreamListing,
        phase: 'open',
      });

      setActiveContact({
        listing: listing as PropertyDetailListing | ExtendedListing | null | undefined,
        title: title ?? 'Contact property',
      });
    },
    [currentUser],
  );

  const openShare = useCallback((listing: ListingActionListing | null | undefined) => {
    if (!listing) return;
    const clickstreamListing = getListingActionClickstreamListing(listing);

    analytics.emitTrackShareClickEvent({
      listing: clickstreamListing,
      triggerLocation: 'home_details',
      triggerObject: 'home_details_component|action_bar',
    });
    if (tryNativeShare(listing)) {
      emitTrackHdpNativeShareEvent({ listing: clickstreamListing });
      return;
    }

    setActiveShare({
      listing: listing as PropertyDetailListing | ExtendedListing,
    });
  }, []);

  const isListingSaved = useCallback(
    (listing: ListingLike | null | undefined) => {
      if (!currentUser?.loggedIn) return false;
      const alias = getListingActionAlias(listing);
      if (!alias) return false;

      const override = favoriteOverrides[alias];
      if (override !== undefined) return override;

      return favoritedAliases.has(alias) || listingHasFavoriteUserItem(listing);
    },
    [currentUser?.loggedIn, favoriteOverrides, favoritedAliases],
  );

  const isListingSavePending = useCallback(
    (listing: ListingLike | null | undefined) => {
      const alias = getListingActionAlias(listing);
      return !!alias && pendingFavorites.some((pendingFavorite) => pendingFavorite.alias === alias);
    },
    [pendingFavorites],
  );

  // Yields (work runs after the tap's frame paints): the sync auth-modal mount +
  // analytics emission was the page's worst INP contributor (~255ms processing
  // at 4x CPU). Nothing here needs the gesture's transient activation.
  const toggleSaveListing = useCallback(
    async (
      listing: ListingLike | null | undefined,
      analyticsContext: ListingActionAnalyticsContext = DEFAULT_LISTING_ACTION_ANALYTICS,
    ) => {
      await interactionResponse();
      const alias = getListingActionAlias(listing);
      if (!listing || !alias) return;
      const clickstreamListing = getListingActionClickstreamListing(listing);
      const analyticsContextWithListing = { ...analyticsContext, listing: clickstreamListing };

      const listingSaved = isListingSaved(listing);
      if (listingSaved) {
        analytics.emitTrackUnSaveHomeClickEvent(analyticsContextWithListing);
        void removeFavorite(alias);
        return;
      }

      analytics.emitTrackSaveHomeClickEvent(analyticsContextWithListing);

      const nextPendingFavorite = {
        alias,
        analyticsContext: analyticsContextWithListing,
        requestedAt: Date.now(),
      };

      if (!currentUser?.loggedIn) {
        setPendingFavorites((currentPendingFavorites) => {
          const nextPendingFavorites = mergePendingFavorites(currentPendingFavorites, [nextPendingFavorite]);
          writePendingFavorites(nextPendingFavorites);
          return nextPendingFavorites;
        });
        return;
      }

      void addFavorite(nextPendingFavorite);
    },
    [addFavorite, currentUser, isListingSaved, removeFavorite],
  );

  const handleAuthModalOpenChange = useCallback((open: boolean) => {
    setAuthModalOpen(open);
    if (open) return;

    setPendingFavorites([]);
    clearPendingFavorites();
  }, []);

  const handleContactSubmitSuccess = useCallback((context: ContactSubmissionContext) => {
    setActiveContact(null);
    setPostContactContext(context);
  }, []);

  const value = useMemo(
    () => ({
      openContact,
      openShare,
      openReport,
      isListingSaved,
      isListingSavePending,
      isListingHidden,
      reconcileFavoriteOverride,
      toggleSaveListing,
      toggleHideListing,
    }),
    [
      openContact,
      openShare,
      openReport,
      isListingSaved,
      isListingSavePending,
      isListingHidden,
      reconcileFavoriteOverride,
      toggleSaveListing,
      toggleHideListing,
    ],
  );

  return (
    <ListingActionContext.Provider value={value}>
      {children}
      <Suspense fallback={null}>
        {activeContact && (
          <ContactFormOverlay
            open
            onOpenChange={(open) => !open && setActiveContact(null)}
            title={activeContact.title}
            listing={activeContact.listing}
            mode="contact"
            onSubmitSuccess={handleContactSubmitSuccess}
          />
        )}
        {postContactContext && (
          <PostContactFlowModal context={postContactContext} onDone={() => setPostContactContext(null)} />
        )}
        {activeShare && (
          <ShareFormOverlay open onOpenChange={(open) => !open && setActiveShare(null)} listing={activeShare.listing} />
        )}
        {activeReport && (
          <ReportListingOverlay
            open
            onOpenChange={(open) => !open && setActiveReport(null)}
            listing={activeReport.listing}
          />
        )}
        <ListingActionSignInModal
          open={authModalOpen}
          onOpenChange={handleAuthModalOpenChange}
          onAuthenticated={completePendingFavorites}
          postLoginRedirect={authRedirectPath}
        />
        <ListingActionSignInModal
          open={hideAuthModalOpen}
          onOpenChange={handleHideAuthModalOpenChange}
          onAuthenticated={completePendingHide}
          postLoginRedirect={hideAuthRedirectPath}
          title={tAuth('hidePromptTitle')}
          intro={tAuth('hidePromptIntro')}
          testId="hide-sign-in-modal"
        />
      </Suspense>
    </ListingActionContext.Provider>
  );
}

export function useListingActionContext(): ListingActionContextValue {
  const context = useContext(ListingActionContext);

  if (!context) {
    throw new Error('useListingActionContext must be used within a ListingActionProvider');
  }

  return context;
}

export function useOptionalListingActionContext(): ListingActionContextValue | null {
  return useContext(ListingActionContext);
}

export type { ListingActionAnalyticsContext, ListingActionListing, ListingActionOpenOptions };
